import { useParams, Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import { categories, articles, hotArticles } from "@/mocks/articles";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  const categoryArticles = articles.filter((a) => a.categoryId === category?.id);
  const categoryHotArticles = hotArticles.filter((a) => a.categoryId === category?.id).slice(0, 3);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading font-black text-4xl text-foreground-950 mb-4">分类不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { name: "分类", href: "/category/star-gossip" },
    { name: category.name },
  ];

  const schema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} - 53吃瓜`,
    description: category.description,
    url: typeof window !== "undefined" ? window.location.href : "",
    isPartOf: {
      "@type": "WebSite",
      name: "53吃瓜",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: categoryArticles.map((article, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `${typeof window !== "undefined" ? window.location.origin : ""}/article/${article.id}`,
        name: article.title,
      })),
    },
  };

  return (
    <>
      <SEOHead
        title={`${category.name} - 53吃瓜 | 娱乐八卦爆料追踪`}
        description={category.description}
        jsonLd={schema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-20 pb-16 md:pb-24 bg-foreground-950 min-h-[400px] md:min-h-[500px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20dark%20theater%20curtain%20background%2C%20red%20velvet%20texture%2C%20subtle%20spotlight%20beam%2C%20dramatic%20moody%20atmosphere%2C%20entertainment%20industry%20vibes%2C%20cinematic%20style%2C%20warm%20red%20tones&width=1600&height=900&seq=category-hero&orientation=landscape"
            alt={category.name}
            className="w-full h-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="font-heading font-black text-white text-4xl md:text-5xl lg:text-6xl leading-tight mt-4 mb-4">
              {category.name}
            </h1>
            <p className="text-white/70 text-base md:text-lg max-w-xl mb-6">
              {category.description}
            </p>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-white/90 text-sm">
              <i className="ri-article-line" />
              {categoryArticles.length} 篇爆料
            </span>
          </div>
        </div>
      </section>

      <main className="bg-background-50">
        {/* Category Tabs */}
        <section className="py-6 md:py-8 border-b border-background-200 bg-background-50">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                      cat.slug === slug
                        ? "bg-primary-500 text-white shadow-sm"
                        : "bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-foreground-800"
                    }`}
                  >
                    <i className={cat.icon} />
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hot Articles */}
        {categoryHotArticles.length > 0 && (
          <section className="py-12 md:py-16">
            <div className="w-full px-4 md:px-8 lg:px-12">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading font-black text-foreground-950 text-2xl md:text-3xl mb-8">
                  🔥 {category.name}热门
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {categoryHotArticles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group relative overflow-hidden rounded-2xl"
                    >
                      <div className="aspect-[4/3]">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        <div className="absolute top-3 left-3">
                          <span className="px-2 py-1 rounded-full bg-primary-500 text-white text-xs font-bold">爆</span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <h3 className="font-heading font-bold text-white text-base leading-snug group-hover:text-primary-300 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* All Articles */}
        <section className="pb-16 md:pb-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading font-black text-foreground-950 text-2xl md:text-3xl mb-8">
                {category.name}最新
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryArticles.map((article) => (
                  <article key={article.id} className="group">
                    <Link to={`/article/${article.id}`} className="block">
                      <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all hover:shadow-lg">
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="p-5">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-800 text-xs font-medium">
                              {article.categoryName}
                            </span>
                            <span className="text-foreground-400 text-xs">{article.publishDate}</span>
                          </div>
                          <h3 className="font-heading font-bold text-foreground-950 text-base leading-snug mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200">
                            <span className="text-foreground-400 text-xs">{article.author}</span>
                            <div className="flex items-center gap-3 text-foreground-400 text-xs">
                              <span className="flex items-center gap-1">
                                <i className="ri-eye-line" />
                                {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}